import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import translations from '../locales/translations.json';

type Language = 'en' | 'tr' | 'de';

interface TranslationContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  availableLanguages: Language[];
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('en');

  // Load language from localStorage or detect browser language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('app_language') as Language;
    
    if (savedLanguage && ['en', 'tr', 'de'].includes(savedLanguage)) {
      // Use saved language from localStorage
      setCurrentLanguage(savedLanguage);
    } else {
      // Detect browser language and set default
      const browserLanguage = navigator.language.toLowerCase();
      let detectedLanguage: Language = 'en'; // default fallback
      
      // Map browser language to our supported languages
      if (browserLanguage.startsWith('tr') || browserLanguage.startsWith('tr-tr')) {
        detectedLanguage = 'tr';
      } else if (browserLanguage.startsWith('de') || browserLanguage.startsWith('de-de')) {
        detectedLanguage = 'de';
      } else if (browserLanguage.startsWith('en') || browserLanguage.startsWith('en-us') || browserLanguage.startsWith('en-gb')) {
        detectedLanguage = 'en';
      }
      
      // Set detected language and save to localStorage
      setCurrentLanguage(detectedLanguage);
      localStorage.setItem('app_language', detectedLanguage);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setCurrentLanguage(lang);
    localStorage.setItem('app_language', lang);
    
    // Always update currency based on new language
    const getDefaultCurrencyForLanguage = (language: string) => {
      switch (language) {
        case 'tr':
          return 'TRY';
        case 'de':
          return 'EUR';
        case 'en':
        default:
          return 'USD';
      }
    };
    
    const defaultCurrency = getDefaultCurrencyForLanguage(lang);
    localStorage.setItem('selectedCurrency', defaultCurrency);
    
    // Force page reload to update currency context
    window.location.reload();
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Split the key by dots to navigate nested objects
    const keys = key.split('.');
    let value: any = translations;

    // Navigate through the nested object structure
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // If key not found, return the key itself
        return key;
      }
    }

    // If we found a translation object with language keys
    if (value && typeof value === 'object' && currentLanguage in value) {
      let translatedText = value[currentLanguage] as string;

      // Replace parameters if provided
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          translatedText = translatedText.replace(`{${paramKey}}`, String(paramValue));
        });
      }

      return translatedText;
    }

    // If no translation found, return the key
    return key;
  };

  const availableLanguages: Language[] = ['en', 'tr', 'de'];

  const value: TranslationContextType = {
    t,
    currentLanguage,
    setLanguage,
    availableLanguages
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}; 